import { PromptTabs } from "@1js/pl-card-components";
import type { Group } from "@1js/pl-types";
import { useMsal } from "@azure/msal-react";
import React from "react";
import { useFeatureEnablement } from "../../hooks/useFeatureEnablement";
import { MetaOSAppContext } from "../../pages/metaos/MetaOSAppContext";
import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import { fetchGroups } from "../../services/prompt-client.web";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import type { HandlePromptsTabSelected } from "./AllPrompts.web.data";

export type UseWorkgroupProps = {
  handlePromptsTabSelected: HandlePromptsTabSelected;
  isRunningOnWorkgroupTab?: boolean;
};

export const useWorkgroups = ({
  handlePromptsTabSelected,
  isRunningOnWorkgroupTab = false,
}: UseWorkgroupProps) => {
  const telemetry = useTelemetry();
  const { state, dispatch } = React.useContext(LibraryContext);
  const { isWorkgroupPublishingEnabled } = useFeatureEnablement();

  const metaOSAppContext = React.useContext(MetaOSAppContext);
  const msalInstance = useMsal();
  const instance =
    metaOSAppContext.isMosApp && metaOSAppContext.pca
      ? msalInstance.instance
      : undefined;

  const fetchGroupsMemberAsync = React.useCallback(async () => {
    if (
      state.serviceHost &&
      state.locale &&
      state.groupMemberStatus === "idle" &&
      isWorkgroupPublishingEnabled
    ) {
      try {
        dispatch({ type: "REQUEST_ALL_GROUPS_MEMBER_INIT" });

        // resolve all requests
        const groups = await fetchGroups(
          state.serviceHost,
          state.locale,
          true,
          telemetry.sendApiActionEvent,
          instance
        );

        dispatch({
          type: "REQUEST_ALL_GROUPS_MEMBER_SUCCEED",
          payload: { groups: [...groups.Workgroups] },
        });
      } catch (error) {
        console.error("Error fetching owned groups:", error);
        dispatch({ type: "REQUEST_ALL_GROUPS_MEMBER_FAILED" });
      }
    }
    // disable exhaustive deps because we don't want to refetch prompts when promptSettings changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.groupMemberStatus, state.serviceHost, state.locale]);

  const fetchGroupsHasPromptsAsync: () => Promise<Group[] | undefined> =
    React.useCallback(async () => {
      if (
        state.serviceHost &&
        state.locale &&
        state.groupHasPromptsStatus === "idle" &&
        isWorkgroupPublishingEnabled
      ) {
        try {
          dispatch({ type: "REQUEST_ALL_GROUPS_HASPROMPTS_INIT" });

          // resolve all requests
          const groups = await fetchGroups(
            state.serviceHost,
            state.locale,
            false,
            telemetry.sendApiActionEvent,
            instance
          );

          dispatch({
            type: "REQUEST_ALL_GROUPS_HASPROMPTS_SUCCEED",
            payload: { groups: [...groups.Workgroups] },
          });

          return groups.Workgroups;
        } catch (error) {
          console.error("Error fetching groups with prompts:", error);
          dispatch({ type: "REQUEST_ALL_GROUPS_HASPROMPTS_FAILED" });
          return undefined;
        }
      }
      // disable exhaustive deps because we don't want to refetch prompts when promptSettings changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.groupHasPromptsStatus, state.serviceHost, state.locale]);

  React.useEffect(() => {
    fetchGroupsMemberAsync();
  }, [fetchGroupsMemberAsync]);

  React.useEffect(() => {
    const getGroupsHasPrompts = async () => {
      if (
        isWorkgroupPublishingEnabled &&
        state.groupHasPromptsStatus === "idle" &&
        state.selectedTab === PromptTabs.GroupPrompts &&
        isRunningOnWorkgroupTab
      ) {
        // state.selectedTab === PromptTabs.GroupPrompts means that we should display the Workgroup tab
        // but it does not guarantee that the Workgroup tab is currently selected.
        const groupsHasPrompts = await fetchGroupsHasPromptsAsync();
        const sortedGroupsHasPrompts = groupsHasPrompts?.sort(
          (a: Group, b: Group) => a.DisplayName.localeCompare(b.DisplayName)
        );
        handlePromptsTabSelected(
          PromptTabs.GroupPrompts,
          sortedGroupsHasPrompts
        );
      }
    };
    getGroupsHasPrompts();
  }, [
    dispatch,
    state.selectedTab,
    state.groups,
    state.groupHasPromptsStatus,
    fetchGroupsHasPromptsAsync,
    handlePromptsTabSelected,
    isRunningOnWorkgroupTab,
    isWorkgroupPublishingEnabled,
  ]);

  return {
    fetchGroupsMemberAsync,
    fetchGroupsHasPromptsAsync,
  };
};
